import React from 'react';
import { Box, Typography, Avatar, keyframes } from '@mui/material';
import {
  SmartToy as BotIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import MarkdownRenderer from './MarkdownRenderer';
import '../../styles/rag-components.css';

// Typing animation keyframes
const typingAnimation = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
`;

interface ChatMessageProps {
  message: {
    id: number;
    type: 'user' | 'assistant';
    message: string;
    sources?: any[];
    document_references?: number[];
    timestamp: string;
    is_error?: boolean;
    processing_time_ms?: number;
    feedback?: {
      feedback_type: string;
      note?: string;
      created_at: string;
    };
    citations?: any[]; // Phase 2 citations
    mindmap_markdown?: string | null; // Phase 2 mindmap
    mindmap_metadata?: any; // Phase 2 mindmap metadata
    metadata?: any; // Phase 2 metadata
  };
  onSourceClick?: (source: any) => void;
  formatTimestamp: (timestamp: string) => string;
  parseContent: (content: string) => string;
  children?: React.ReactNode; // For MessageFeedback component
  isLoading?: boolean; // New prop for loading state
  projectDocuments?: any[]; // Add project documents for name resolution
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onSourceClick,
  formatTimestamp,
  parseContent,
  children,
  isLoading = false,
  projectDocuments = [],
}) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start',
        gap: 2,
        flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
      }}>
        <Avatar sx={{ 
          bgcolor: message.type === 'user' ? 'primary.main' : 'success.main',
          width: 40,
          height: 40,
          flexShrink: 0,
        }}>
          {message.type === 'user' ? <PersonIcon /> : <BotIcon />}
        </Avatar>
        
        <Box
          sx={{
            maxWidth: '75%',
            bgcolor: message.type === 'user' ? 'primary.main' : 'background.paper',
            color: message.type === 'user' ? 'primary.contrastText' : 'text.primary',
            borderRadius: 2,
            p: 2.5,
            position: 'relative',
            boxShadow: message.type === 'user' 
              ? '0 2px 8px rgba(25, 118, 210, 0.15)' 
              : '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: message.type === 'assistant' ? '1px solid' : 'none',
            borderColor: message.type === 'assistant' ? 'divider' : 'transparent',
            '&::before': message.type === 'user' ? {
              content: '""',
              position: 'absolute',
              top: 20,
              right: -8,
              width: 0,
              height: 0,
              borderLeft: '8px solid',
              borderLeftColor: 'primary.main',
              borderTop: '8px solid transparent',
              borderBottom: '8px solid transparent',
            } : {
              content: '""',
              position: 'absolute',
              top: 20,
              left: -8,
              width: 0,
              height: 0,
              borderRight: '8px solid',
              borderRightColor: 'background.paper',
              borderTop: '8px solid transparent',
              borderBottom: '8px solid transparent',
            },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {message.message}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                {[0, 1, 2].map((index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      animation: `${typingAnimation} 1.4s infinite ease-in-out`,
                      animationDelay: `${index * 0.2}s`,
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <>
              {message.type === 'assistant' && !message.is_error ? (
                <MarkdownRenderer
                  content={message.message}
                  sources={message.metadata?.citations?.citations || message.sources || []}
                  projectDocuments={projectDocuments}
                  messageId={message.id}
                  onSourceClick={(source) => {
                    // Pass citation click to parent (Chat page) to expand in info panel
                    if (onSourceClick) {
                      onSourceClick(source);
                    }
                  }}
                />
              ) : (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {message.message}
                </Typography>
              )}
            </>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {formatTimestamp(message.timestamp)}
              {message.processing_time_ms && (
                <span> • {message.processing_time_ms}ms</span>
              )}
            </Typography>
            
            {message.type === 'assistant' && (
              <></>
            )}
          </Box>


          {/* Feedback for assistant messages - hide during loading */}
          {!isLoading && message.type === 'assistant' && !message.is_error && (message.metadata?.id || message.id) && children}
        </Box>
      </Box>

    </Box>
  );
};

export default ChatMessage;
